/**
 * Application routes for Idle Depths.
 *
 * Defines all routes using React Router.
 * Dialogs (Privacy, Terms, Account) are rendered over the home page.
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { RootLayout } from '@/components/layout/RootLayout';
import { SplashPage } from '@/components/SplashPage';
import { Skeleton } from '@/components/ui/skeleton';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'));
const CharacterSelectionPage = lazy(() => import('@/pages/CharacterSelectionPage'));
const GamePage = lazy(() => import('@/pages/GamePage'));
const SkillsPage = lazy(() => import('@/pages/SkillsPage'));
const SkillDetailPage = lazy(() => import('@/pages/SkillDetailPage'));

/**
 * Loading fallback for lazy-loaded pages.
 */
function PageLoader(): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-4 w-56" />
      </div>
    </div>
  );
}

/**
 * Suspense wrapper for lazy pages.
 */
function LazyPage({ children }: { children: React.ReactNode }): React.ReactElement {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/**
 * Browser router configuration.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Home page (splash for now, will be game later)
      {
        index: true,
        element: <SplashPage />,
      },

      // Dialog pages - show SplashPage with dialog overlay (handled by RootLayout)
      {
        path: 'privacy',
        element: <SplashPage />,
      },
      {
        path: 'terms',
        element: <SplashPage />,
      },
      {
        path: 'account',
        element: <SplashPage />,
      },

      // Auth pages
      {
        path: 'login',
        element: (
          <LazyPage>
            <LoginPage />
          </LazyPage>
        ),
      },
      {
        path: 'auth/callback',
        element: (
          <LazyPage>
            <AuthCallbackPage />
          </LazyPage>
        ),
      },

      // Character management
      {
        path: 'characters',
        element: (
          <LazyPage>
            <CharacterSelectionPage />
          </LazyPage>
        ),
      },

      // Main game view
      {
        path: 'game',
        element: (
          <LazyPage>
            <GamePage />
          </LazyPage>
        ),
      },

      // Skills pages
      {
        path: 'game/skills',
        element: (
          <LazyPage>
            <SkillsPage />
          </LazyPage>
        ),
      },
      {
        path: 'game/skills/:skillId',
        element: (
          <LazyPage>
            <SkillDetailPage />
          </LazyPage>
        ),
      },

      // Catch-all redirect to home
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);
